import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Zap, Shield, Clock, Bot, CheckCircle2, Users, Brain,
  Quote, Star,
} from 'lucide-react';
import StatsBar from '../components/shared/StatsBar';
import ClientLogos from '../components/shared/ClientLogos';
import CTABanner from '../components/shared/CTABanner';
import TechVideoBackground from '../components/shared/TechVideoBackground';
import WaveDivider from '../components/shared/WaveDivider';
import TechPattern from '../components/shared/TechPattern';

const products = [
  {
    title: 'AI Engineering',
    description: 'Software engineers powered by AI. Custom solutions engineered for your specific business processes, from architecture to deployment.',
    icon: Brain,
    color: '#2563eb',
    gradient: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
    features: ['Custom AI-powered development', 'API & system integration', 'Scalable infrastructure', 'Performance optimization'],
    image: 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 100%)',
  },
  {
    title: 'AI Teams',
    description: 'Software engineers working alongside AI agents. A complete AI-powered workforce custom-built for your company.',
    icon: Users,
    color: '#7c3aed',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
    features: ['End-to-end AI operations', 'Engineers + AI agents combined', 'Knowledge base integration', 'Enterprise-grade security'],
    image: 'linear-gradient(135deg, #ede9fe 0%, #fce7f3 100%)',
  },
];

const differentiators = [
  { title: 'Produce', desc: 'Agents handle complete tasks: reviews, reports, verifications, automations.', color: '#2563eb' },
  { title: 'Validate', desc: 'Agents cross-validate each other. If validation fails, the work returns with feedback.', color: '#0891b2' },
  { title: 'Report', desc: 'Objective metrics: accuracy rate, speed, volume. You know exactly the quality.', color: '#ea580c' },
];

const testimonials = [
  {
    quote: 'We went from a 2-week code review backlog to same-day reviews. The agents catch issues our team was missing.',
    name: 'Sarah Chen',
    role: 'VP of Engineering',
    company: 'Globo.com',
  },
  {
    quote: 'The diagnostic onboarding blew my mind. In 15 minutes, the system understood our processes better than a new hire does after a month.',
    name: 'Priya Patel',
    role: 'Director of Engineering',
    company: 'LuizaLabs',
  },
  {
    quote: 'Having AI agents working alongside our engineers transformed how we handle code reviews and deployments. The consistency is remarkable.',
    name: 'Marcus Rivera',
    role: 'CTO',
    company: 'LuizaLabs',
  },
];

const HomePage: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: 72,
        background: 'var(--bg-secondary)',
      }}>
        <TechVideoBackground variant="hero" opacity={0.6} />
        <div className="gradient-mesh" />
        <div className="circuit-pattern" />
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
        <div className="hero-orb hero-orb-3" />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ maxWidth: 840, margin: '0 auto', textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label">
                <Bot size={14} />
                Custom AI Agent Teams for Your Business
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              style={{
                fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                fontWeight: 900,
                lineHeight: 1.1,
                marginBottom: 24,
                letterSpacing: '-0.02em',
              }}
            >
              Your{' '}
              <span className="gradient-text">AI Agent Team</span>
              <br />
              Working 24/7
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              style={{
                fontSize: 'clamp(1.1rem, 2vw, 1.3rem)',
                color: 'var(--text-secondary)',
                maxWidth: 660,
                margin: '0 auto 40px',
                lineHeight: 1.7,
              }}
            >
              We build custom teams of AI agents tailored to your operations.
              They learn your processes, coordinate with each other,
              and deliver with{' '}
              <strong style={{ color: 'var(--accent-blue)' }}>measurable quality metrics</strong>.
              Setup in minutes.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: 32,
              }}
            >
              <Link
                to="/contact"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '16px 32px',
                  background: 'var(--gradient-blue)',
                  borderRadius: 100,
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  color: 'white',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(37, 99, 235, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.25)';
                }}
              >
                Schedule Free Diagnostic <ArrowRight size={18} />
              </Link>
              <Link
                to="/products"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '16px 32px',
                  background: 'white',
                  border: '1px solid var(--border-color)',
                  borderRadius: 100,
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(37, 99, 235, 0.3)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                }}
              >
                Explore Our Products
              </Link>
            </motion.div>

            {/* Key differentiator */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="glass-card"
              style={{
                padding: '16px 28px',
                borderRadius: 16,
                maxWidth: 640,
                margin: '0 auto 48px',
              }}
            >
              <p style={{
                fontSize: '1rem',
                color: 'var(--accent-blue)',
                fontWeight: 600,
                lineHeight: 1.5,
                margin: 0,
              }}>
                Other tools give AI to your team to use.
                We give you an AI team to work alongside yours.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32,
                flexWrap: 'wrap',
                marginBottom: 60,
              }}
            >
              {[
                { icon: <Clock size={16} />, text: 'Available 24/7' },
                { icon: <Zap size={16} />, text: 'Setup in Minutes' },
                { icon: <Shield size={16} />, text: '100% Isolated Data' },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem',
                }}>
                  <span style={{ color: 'var(--accent-blue)' }}>{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </motion.div>
          </div>

          <StatsBar />
        </div>
      </section>

      {/* Client Logos Marquee */}
      <ClientLogos />

      {/* Wave into products */}
      <WaveDivider from="var(--bg-primary)" to="var(--bg-primary)" />

      {/* Products Preview with image placeholders */}
      <section className="section" style={{ paddingTop: 60, position: 'relative', overflow: 'hidden' }}>
        <TechPattern variant="hexagon" opacity={0.03} />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', marginBottom: 64 }}
          >
            <span className="section-label">
              <Zap size={14} />
              Our Products
            </span>
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>
              Two Ways to <span className="gradient-text">Transform</span> Your Operations
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              Whether you need AI-powered software engineers or a complete AI workforce,
              we have the solution for your business.
            </p>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 24,
            marginBottom: 48,
            maxWidth: 800,
            margin: '0 auto 48px',
          }}>
            {products.map((product, i) => {
              const Icon = product.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="gradient-border-card"
                  style={{
                    padding: 0,
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-card)',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s',
                  }}
                >
                  {/* Card image/gradient area */}
                  <div className="img-zoom" style={{
                    borderRadius: 0,
                    borderTopLeftRadius: 'var(--radius-lg)',
                    borderTopRightRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                  }}>
                    <div
                      className="img-placeholder"
                      style={{
                        height: 160,
                        background: product.image,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      <div style={{
                        width: 72,
                        height: 72,
                        borderRadius: 20,
                        background: product.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 8px 24px ${product.color}25`,
                      }}>
                        <Icon size={36} color="white" />
                      </div>
                      {i === 1 && (
                        <div style={{
                          position: 'absolute', top: 12, right: 12,
                          padding: '4px 12px', background: product.gradient,
                          borderRadius: 100, fontSize: '0.7rem', fontWeight: 700, color: 'white',
                        }}>
                          Most Popular
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card content */}
                  <div style={{ padding: '24px 24px 28px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 10 }}>{product.title}</h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20, flex: 1 }}>
                      {product.description}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                      {product.features.map((f, j) => (
                        <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <CheckCircle2 size={14} color={product.color} />
                          <span style={{ fontSize: '0.83rem', color: 'var(--text-muted)' }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/products" style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 20px', background: `${product.color}08`,
                      border: `1px solid ${product.color}20`,
                      borderRadius: 12, fontSize: '0.9rem', fontWeight: 600, color: product.color,
                      transition: 'all 0.2s',
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = `${product.color}12`; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = `${product.color}08`; }}
                    >
                      Learn More <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
        <style>{`
          @media (max-width: 768px) {
            section > .container > div[style*="grid-template-columns: repeat(3"] { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      {/* Wave into agents section */}
      <WaveDivider from="var(--bg-primary)" to="var(--bg-secondary)" />

      {/* How Agents Work */}
      <section className="section" style={{ background: 'var(--bg-secondary)', position: 'relative', overflow: 'hidden', paddingTop: 60 }}>
        <div className="dots-pattern" />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: 48 }}
          >
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>
              How Our <span className="gradient-text-cyan">Agents Work</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              Agents coordinate with each other. One produces, another validates, another reports. You monitor.
            </p>
          </motion.div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 20, maxWidth: 900, margin: '0 auto',
          }}>
            {differentiators.map((block, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                style={{
                  background: 'white', borderRadius: 'var(--radius)',
                  padding: '32px 24px', border: '1px solid var(--border-color)',
                  boxShadow: 'var(--shadow-card)', textAlign: 'center',
                }}
              >
                <div style={{
                  width: 52, height: 52, borderRadius: 14, background: `${block.color}0a`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
                }}>
                  <CheckCircle2 size={24} color={block.color} />
                </div>
                <h4 style={{ fontSize: '1.15rem', fontWeight: 700, color: block.color, marginBottom: 8 }}>{block.title}</h4>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>{block.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              maxWidth: 700, margin: '48px auto 0', padding: '24px 32px',
              background: 'white', border: '1px solid var(--border-color)',
              borderRadius: 16, textAlign: 'center', boxShadow: 'var(--shadow-card)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
              {[
                { value: '50+', label: 'agents operating' },
                { value: '60%', label: 'tasks automated' },
                { value: '24/7', label: 'continuous operation' },
              ].map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div style={{ width: 1, height: 48, background: 'var(--border-color)' }} />}
                  <div>
                    <div style={{
                      fontSize: '2.5rem', fontWeight: 800, fontFamily: 'Space Grotesk',
                      background: 'var(--gradient-blue)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    }}>{s.value}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{s.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Wave into testimonials */}
      <WaveDivider from="var(--bg-secondary)" to="var(--bg-primary)" />

      {/* Testimonials */}
      <section className="section" style={{ paddingTop: 60, position: 'relative', overflow: 'hidden' }}>
        <TechPattern variant="circles" opacity={0.025} />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: 48 }}
          >
            <span className="section-label"><Star size={14} />What Clients Say</span>
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>
              Trusted by <span className="gradient-text">Engineering Leaders</span>
            </h2>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
          }}>
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                style={{
                  background: 'white',
                  borderRadius: 'var(--radius-lg)',
                  padding: '32px 28px',
                  border: '1px solid var(--border-color)',
                  boxShadow: 'var(--shadow-card)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Stars */}
                <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={16} fill="#f59e0b" color="#f59e0b" />
                  ))}
                </div>

                <Quote size={20} color="var(--accent-blue)" style={{ opacity: 0.2, marginBottom: 12 }} />
                <p style={{
                  fontSize: '0.95rem',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7,
                  flex: 1,
                  marginBottom: 24,
                  fontStyle: 'italic',
                }}>
                  "{t.quote}"
                </p>

                <div style={{
                  borderTop: '1px solid var(--border-color)',
                  paddingTop: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: 'var(--gradient-blue)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    fontFamily: 'Space Grotesk',
                  }}>
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{t.name}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t.role}, {t.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 768px) {
            section > .container > div[style*="grid-template-columns: repeat(3"] { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      <CTABanner />
    </>
  );
};

export default HomePage;
